/**
 * Long stretches of transcript with no words in them.
 *
 * The engine emits a "spacer" for every stretch between words, and the panel
 * used to render all of them as dots on a tier system: three dots past 2s, six
 * past 10s, nine past 30s. Nobody reads that. Six dots versus nine is a
 * counting exercise, and even three versus six needs a deliberate look — so
 * three tiers of information arrived as one tier of "something was skipped".
 *
 * So: two tiers. Short gaps keep the dots, where the point is only that a beat
 * passed. Anything past TRANSCRIPTION_GAP_ROW_MIN_DURATION_MS gets a row of
 * its own — a rule across the panel with the duration written into it, which
 * says the one thing dot-counting could never say: how long.
 *
 * The wording is deliberately NOT "silence". All the transcript tells us is
 * that no words were transcribed; the audio may be full of noise, music, or
 * speech the engine could not resolve. Claiming silence would be asserting
 * something nobody checked. Inspecting the audio to say more is a possible
 * future feature, and would be what licenses a stronger word than this one.
 */
import { TranscriptionViewableWords } from '@common-modules/mediaDetails/models';
import { TRANSCRIPTION_GAP_ROW_MIN_DURATION_MS } from '@helpers/constants';

export const gapDurationMs = (word: TranscriptionViewableWords) =>
  word.stopTimeMs - word.startTimeMs;

/** A spacer long enough to earn a row instead of dots. */
export const isGapWord = (word: TranscriptionViewableWords | undefined) =>
  !!word?.isSpacer &&
  gapDurationMs(word) >= TRANSCRIPTION_GAP_ROW_MIN_DURATION_MS;

/**
 * Coarse and spoken, not a timestamp: "2m 14s", not "2:14".
 *
 * The gutter beside this row already carries the clock time, so a second
 * `m:ss` would read as another position rather than a length. Seconds are
 * dropped past an hour, where they are noise next to the number in front of
 * them.
 */
export const formatGapDuration = (ms: number): string => {
  const total = Math.max(0, Math.round(ms / 1000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
};
